
/**
 * Module dependencies.
 */

import { BaseDisclosure, BaseDisclosureProps } from './base-disclosure';
import { Icon } from 'react-components/media';
import { Text } from '../typography';
import { color } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import RawHtml from 'react-components/raw-html';
import caretIcon from '../assets/svg/24/caret.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = Omit<BaseDisclosureProps, 'header'> & {
  description?: string;
  title: string;
};

/**
 * `HeaderWrapper` styled component.
 */

const HeaderWrapper = styled.div`
  column-gap: 8px;
  display: flex;
  justify-content: space-between;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({ as: 'span', size: 'normal' })<{ isOpen: boolean }>`
  color: ${color('secondary')};
  font-weight: 700;
  transition: color ${theme('animations.defaultTransition')};

  ${ifProp('isOpen', css`
    color: ${color('primary')};
  `)}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ as: 'div', size: 'small' })`
  color: ${color('gray500')};
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.div<{ isOpen: boolean }>`
  color: ${color('gray500')};
  height: max-content;
  transform: rotate(-0.25turn);
  transform-origin: center;
  transition: ${theme('animations.defaultTransition')};
  transition-property: color, transform;

  ${ifProp('isOpen', css`
    color: ${color('primary')};
    transform: rotate(-0.5turn);
  `)}
`;

/**
 * `Disclosure` component.
 */

export function Disclosure(props: Props): JSX.Element {
  const { children, description, isOpen = false, title, ...rest } = props;

  return (
    <BaseDisclosure
      {...rest}
      header={
        <HeaderWrapper>
          <div>
            <Title isOpen={isOpen}>
              {title}
            </Title>

            {description && (
              <Description>
                <RawHtml>
                  {description}
                </RawHtml>
              </Description>
            )}
          </div>

          <IconWrapper isOpen={isOpen}>
            <Icon
              aria-hidden
              color={'inherit'}
              icon={caretIcon}
              size={'24px'}
            />
          </IconWrapper>
        </HeaderWrapper>
      }
      isOpen={isOpen}
    >
      {children}
    </BaseDisclosure>
  );
}
